const Constants = {
  breadCrumb: "breadCrumb",
  home: "home",
  faq: "faq",
  localbusiness: "localbusiness",
  organization: "organization",
  website: "website",
  course: "course",
};

interface TemplateInterface {
  [Key: string]: (data: any) => unknown;
}
const ListTemplate: TemplateInterface = {};
const Template: TemplateInterface = {};

// Define the ListItem structure for breadcrumbs
ListTemplate[Constants.breadCrumb] = (data: any) => {
  if (!Array.isArray(data)) {
    console.error("Expected data to be an array, but received:", data);
    return [];
  }

  return data.map((item: any, idx: number) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": idx === 0 ? "WebSite" : "WebPage",
      "@id": item.href || "",
      name: item.title || "",
    },
  }));
};

// Define the BreadcrumbList structure
Template[Constants.breadCrumb] = (data: any) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: data.map((item: any, idx: number) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": idx === 0 ? "WebSite" : "WebPage",
      "@id": item.href || "",
      name: item.title || "",
    },
  })),
});
Template[Constants.course] = (data: any) => {
  let title, bannerDescp1, link;
  if (data?.name) {
    title = data?.name;
    bannerDescp1 = data?.description;
    link = data?.url;
  }

  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: title,
    description: bannerDescp1,
    provider: {
      "@type": "Organization",
      name: "Regenesys Education",
      sameAs: `https://www.regenesys.net/${link}`,
      url: "https://www.regenesys.net/",
      logo: "https://www.regenesys.net/_next/image?url=https%3A%2F%2Fuat-inf-dr-lp.s3.us-east-1.amazonaws.com%2Fuser-documents%2Fdr_logo_1_63a3cf42a5.png&w=640&q=75",
    },
    offers: {
      "@type": "Offer",
      category: "Paid",
    },
  };

  // Handle the course mode logic
  if (data?.courseMode) {
    const courseInstances = [];

    if (data?.courseMode?.studyMode.includes("online")) {
      courseInstances.push({
        "@type": "CourseInstance",
        courseMode: "online",
        courseWorkload: `P${data.courseMode.duration}Y`,
      });
    }

    if (data?.courseMode?.studyMode.includes("onsite")) {
      courseInstances.push({
        "@type": "CourseInstance",
        courseMode: "onsite",
        courseWorkload: `P${data.courseMode.duration}Y`,
        location: {
          "@type": "Place",
          name: "Regenesys Education",
          address: {
            "@type": "PostalAddress",
            streetAddress: "165 West Street, Sandton",
            addressLocality: "Johannesburg",
            addressRegion: "Gauteng",
            postalCode: "2031",
            addressCountry: "ZA",
          },
        },
      });
    }

    // Add the course instances to the schema if they exist
    if (courseInstances.length > 0) {
      schema.hasCourseInstance = courseInstances;
    }
  }

  return schema;
};

Template[Constants.home] = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    url: "https://www.regenesys.net/",
    sameAs: [
      "https://www.facebook.com/RegenesysBusinessSchool",
      "https://x.com/RegenesysB",
      "https://www.youtube.com/@regenesys_sa",
      "https://www.linkedin.com/school/regenesysbusinessschool/",
      "https://www.instagram.com/regenesys_sa/",
    ],
    logo: "https://www.regenesys.net/_next/image?url=https%3A%2F%2Fuat-inf-dr-lp.s3.us-east-1.amazonaws.com%2Fuser-documents%2Fdr_logo_1_63a3cf42a5.png&w=640&q=75",
    name: "Regenesys Education",
    description:
      "Regenesys: Explore business, law, public management, finance, and technology degrees at Regenesys school. Find your perfect programme & enrol today!",
    address: [
      {
        "@type": "PostalAddress",
        addressCountry: "ZA",
        addressLocality: "Johannesburg",
        addressRegion: "Gauteng",
        postalCode: "2031",
        streetAddress: "165 West Street, Sandton",
      },
      {
        "@type": "PostalAddress",
        addressCountry: "NG",
        addressLocality: "Lagos",
        addressRegion: "Victoria Island",
        postalCode: "101241",
        streetAddress: "8th Floor, Churchgate Tower 2, 31 Churchgate Street",
      },
      {
        "@type": "PostalAddress",
        addressCountry: "KE",
        addressLocality: "Nairobi",
        addressRegion: "Nairobi County",
        postalCode: "30197",
        streetAddress:
          "1203, 12th Floor, GTC Office Tower, Intersection of Waiyaki Way, Chiromo Ln",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+27 (11) 669 5000",
        email: "info@regenesys.net",
      },
    ],
  };
};

Template[Constants.faq] = (data) => {
  // Check if data is an array
  if (!Array.isArray(data)) {
    console.error("Data is not an array or is undefined", data);
    return {};
  }

  // Function to convert HTML to plain text
  const htmlToPlainText = (html: string) => {
    return html.replace(/<[^>]*>/g, ""); // Strip HTML tags
  };

  const faqs = data;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs?.map((item: any) => {
      return {
        "@type": "Question",
        name: item?.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: htmlToPlainText(item?.description), // Convert HTML to plain text
        },
      };
    }),
  };
};

Template[Constants.localbusiness] = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",

    name: "Digital Regenesys",
    address: [
      {
        "@type": "PostalAddress",
        addressCountry: "ZA",
        addressLocality: "Johannesburg",
        addressRegion: "Gauteng",
        postalCode: "2031",
        streetAddress: "165 West Street, Sandton",
      },
      {
        "@type": "PostalAddress",
        addressCountry: "NG",
        addressLocality: "Lagos",
        addressRegion: "Victoria Island",
        postalCode: "101241",
        streetAddress: "8th Floor, Churchgate Tower 2, 31 Churchgate Street",
      },
      {
        "@type": "PostalAddress",
        addressCountry: "KE",
        addressLocality: "Nairobi",
        addressRegion: "Nairobi County",
        postalCode: "30197",
        streetAddress:
          "1203, 12th Floor, GTC Office Tower, Intersection of Waiyaki Way, Chiromo Ln",
      },
    ],
    image: [
      "https://www.digitalregenesys.com/assets/images/allImages/DR-Logo-black.webp",
    ],
  };
};

Template[Constants.organization] = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    url: "https://www.regenesys.net/",
    logo: "https://www.regenesys.net/_next/image?url=https%3A%2F%2Fuat-inf-dr-lp.s3.us-east-1.amazonaws.com%2Fuser-documents%2Fdr_logo_1_63a3cf42a5.png&w=640&q=75",
  };
};

Template[Constants.website] = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Regenesys Education",
    url: "https://www.regenesys.net/",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.regenesys.net/?s={search_term_string}",
      "query-input": "required name=search_term_string",
    },
    description:
      "Regenesys: Explore business, law, public management, finance, and technology degree at Regenesys school. Find your perfect programme & enrol today!",
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Regenesys Education",
      url: "https://www.regenesys.net/",
      logo: {
        "@type": "ImageObject",
        url: "https://www.regenesys.net/_next/image?url=https%3A%2F%2Fuat-inf-dr-lp.s3.us-east-1.amazonaws.com%2Fuser-documents%2Fdr_logo_1_63a3cf42a5.png&w=640&q=75",
      },
    },
  };
};

export { Template, Constants };
